import {Temp} from "../utils/Temp.js";
import {SimpleEditor} from "../editors/SimpleEditor.js";
import {CategoriesDropDown} from "./CategoriesDropDown.js";
import {DDAction, DDEvent, DropDownStateMachine} from "./DropDownStateMachine.js";
import {TypeSearch} from "./TypeSearch.js";


/**
 * Options:
 * 		categories - definition of categories (see Java Categories class)
 * 		multi {boolean} - allows multiple items to be selected at once
 * 		placeholder {string|function} - string or function returning placeholder used for empty selection (defaults to "-")
 * 		readOnly {boolean} - editor is readonly
 */

export class CategoriesEditor extends SimpleEditor {

	constructor(parentEditor, fieldPath, obj, member, options) {
		super(parentEditor, fieldPath, obj, member, options);
		this.state = new DropDownStateMachine(!!options.multi, !!options.readOnly, fieldPath);
		this.state.listen(this.stateAction.bind(this));
	}

	get multi() {
		return this.options.multi;
	}

	rebuild() {
		super.rebuild();
		this.root.addClass("cats-root");
		this.widget = Temp.tag("div", { clazz: 'cats-container ns' });
		this.enableWidget(!this.readOnly);
		this.root.append(this.widget);

		this.dd = new CategoriesDropDown(this.widget, this.state, this.options.categories, this.field, this);	// appends drop down automatically
		this.updateScreen();

		this.widget.on('focus', this.onFocus.bind(this))
			.on('blur', this.onBlur.bind(this))
			.on('mousedown', this.onMouseDown.bind(this))
			.on('dblclick', function(e) { e.preventDefault(); e.stopPropagation(); });
		this.typeSearch = new TypeSearch(this.widget, this.root, (query) => this.dd.setFilter(query));
	}

	stateAction(action, param) {
		switch (action) {
			case DDAction.DISABLE:
				this.enableWidget(false);
				return;
			case DDAction.ENABLE:
				this.enableWidget(true);
				return;
			case DDAction.HIDE_DROP_DOWN:
				if (this.typeSearch) this.typeSearch.clear();
				break;
		}
	}

	onFocus() {
		this.state.event(DDEvent.WIDGET_FOCUS);
	}

	onBlur() {
		this.state.event(DDEvent.WIDGET_BLUR);
	}

	onMouseDown(evt) {
		this.state.event(DDEvent.WIDGET_MOUSE_DOWN, evt);
	}

	widgetClicked() {
		// console.log("Widget Clicked ...");
		if (this.dd.visible) this.dd.shouldBeHidden(true);
		else this.dd.shouldBeVisible();
	}

	enableWidget(enable) {
		if (this.widget) {
			if (enable) {
				this.widget.removeClass('readonly');
				this.widget.attr("tabindex", this.options.tabindex ? this.options.tabindex : "10");
			} else {
				this.widget.addClass('readonly');
				this.widget.removeAttr("tabindex");
			}
			if (this.typeSearch) this.typeSearch.enable(enable);
		}
	}

	/**
	 * Checkbox in the drop down was (un)selected
	 */
	selectionChanged(id, checked, labelClickedFirst) {
		this.updateData();
		this.updateBox();
		if (labelClickedFirst) this.state.event(DDEvent.SELECTION_DONE);
	}


	updateData() {
		this.data = this.dd.selectedValues();
	}

	updateScreen() {
		this.updateBox();
		this.updateDropdown();
	}

	normalizedData() {
		let d = this.data;
		if (typeof d === "undefined") d = [];
		else if (typeof d === "string") d = [d];
		return d;
	}

	/**
	 * Update content of visible field
	 */
	updateBox() {
		this.widget.empty();
		let d = this.normalizedData();
		const cs = this.options.categories;

		if (d.length > 1) this.widget.addClass("cats-many");
		else this.widget.removeClass("cats-many");

		if (d.length === 0) {
			const tg = Temp.tag("span", { clazz: "cats-tag cats-empty" });
			tg.text(this.emptyTitle());
			this.widget.append(tg);
		} else {
			for (let id of d) {
				const found = this.findCategoryById(id, cs.children);
				if (found != null) {
					const tg = Temp.tag("span", { clazz: "cats-tag" });
					tg.text(found.title);
					this.widget.append(tg);
				}
			}
		}
	}

	findCategoryById(id, charray) {
		if (typeof charray !== "undefined" && charray.length > 0) {
			for (let ch of charray) {
				if (id === ch.id) return ch;
				const found = this.findCategoryById(id, ch.children);
				if (found != null) return found;
			}
		}
		return null;
	}

	updateDropdown() {
		const d = this.normalizedData();
		this.dd.updateScreen(d);
	}

	emptyTitle() {
		if (typeof this.options.placeholder === "string") return this.options.placeholder;
		else if (typeof this.options.placeholder === "function") return this.options.placeholder();
		return "-";
	}
}